import React, { useCallback } from 'react';
import { AssigneeDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments/AssigneeDto';
import { AssigneeAvatar } from './AssigneeAvatar';
import { getAssigneePrimaryText, getAssigneeSecondaryText } from './utils/AssignmentUtils';
import './Assignee.less';
import { RestorableListItem } from '../lists/RestorableListItem';
import classNames from 'classnames';
import { State } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments/State';

interface IEditorAssigneeProps extends Omit<React.HTMLProps<HTMLLIElement>, 'onClick'> {
  /**
   * TODO alex: need to rename this modifier to suggestion.
   * In this case, we need to remove border of item and change background
   */
  isSuggestion?: boolean;
  /**
   * New assignee has "asterisk" badge
   */
  new?: boolean;
  assignee: AssigneeDto;
  /**
   * Determines restore button visibility.
   * Besides that, visibility depends on assignee state, if state == Excluded it will be hidden as well.
   * @default true
   */
  canRestore?: boolean;
  isFocused?: boolean;
  onClick?: (assignee: AssigneeDto) => void;
  /**
   * Called when an assignee is restored/removed
   * @param isDeleted New assignee state
   * @param e Original event
   */
  onRestore?: (isDeleted: boolean, assignee: AssigneeDto, e: React.MouseEvent<HTMLButtonElement>) => void;
}

const getClassName = (state: State, isClickable?: boolean, isFocused?: boolean, className?: string) =>
  classNames('dpc-assignee', className, {
    'dpc-assignee--suggestion': isClickable,
    'dpc-assignee--removed': state === State.Removed,
    'dpc-assignee--excluded': state === State.Excluded,
    'dpc-assignee--focused': isFocused,
  });

const getAvatarClassName = (isNew?: boolean) =>
  classNames('dpc-assignee__avatar', {
    'dpc-assignee__avatar--new': isNew,
  });

function getIconTitle(state: State) {
  switch (state) {
    case State.Removed:
      return 'Restore assignee';
    case State.Added:
    case State.Initial:
      return 'Remove assignee';
    default:
      return '';
  }
}

/**
 * An Assignee (List item)
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function Assignee({
  assignee,
  className,
  isSuggestion,
  isFocused,
  onClick,
  canRestore = true,
  onRestore,
  new: isNew,
  ...otherProps
}: IEditorAssigneeProps) {
  const primaryText = getAssigneePrimaryText(assignee);
  const secondaryText = getAssigneeSecondaryText(assignee);
  const handleClick = useCallback(() => onClick && onClick(assignee), [assignee, onClick]);

  return (
    <RestorableListItem
      className={getClassName(assignee.State, isSuggestion, isFocused, className)}
      // this uses mouseDown to avoid race conditions between a "click" and an "onBlur" in the SearchBox implementation
      onMouseDown={handleClick}
      onRestore={useCallback((isDeleted, e) => onRestore && onRestore(isDeleted, assignee, e), [assignee, onRestore])}
      deleted={assignee.State === State.Removed}
      canRestore={canRestore && assignee.State !== State.Excluded}
      iconProps={{ title: getIconTitle(assignee.State) }}
      data-testid={assignee.Id}
      {...otherProps}
    >
      <AssigneeAvatar assignee={assignee} className={getAvatarClassName(isNew)} />
      <div className="dpc-assignee__body">
        {primaryText && <div className="dpc-assignee__primary-text">{primaryText}</div>}
        {secondaryText && <div className="dpc-assignee__secondary-text">{secondaryText}</div>}
      </div>
    </RestorableListItem>
  );
}
